import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { ensureUserGraphemeMeaningCard } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.create";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { eq, inArray } from "@-/com.npmjs.drizzle-orm";
import { isNonEmpty } from "@-/com.npmjs.ts-array-length";

export type UserCardActionTarget =
  | { front: string; kind: "morpheme" }
  | { kind: "user-card"; userCardId: string };

/**
 * Mutations backing user-card actions: skip, reset SRS progress, or toggle manual mastery
 * (`markMastered` / `unmarkMastered`). A morpheme target resolves (and, if needed, creates) its
 * meaning card to preserve the existing bundle-level behavior. An exact target resolves only the
 * supplied `userCardId`, allowing deck meaning and pronunciation cards to be changed separately.
 * Manual mastery is a fully reversible flag
 * (`user_card.manuallyMasteredAt`) — see `lib/cards/user-card/mastery` for the
 * predicate that unifies it with natural FSRS mastery.
 */
export function useUserCardActions() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);

  const resolveUserCard = async (target: UserCardActionTarget) => {
    if (target.kind === "morpheme") {
      return ensureUserGraphemeMeaningCard(actorDb, target.front);
    }
    const userCard = await actorDb(import.meta.location).query.userCard.findFirst({
      where: { id: { eq: target.userCardId } },
    });
    if (userCard === undefined) {
      throw new Error(`User card ${target.userCardId} does not exist`);
    }
    return userCard;
  };

  const skipMutation = useMutation({
    mutationFn: async (target: UserCardActionTarget): Promise<void> => {
      const nowIso = new Date().toISOString();
      const userCard = await resolveUserCard(target);
      await actorDb(import.meta.location)
        .update(actorSchema.userCard)
        .set({ deletedAt: null, skippedAt: nowIso })
        .where(eq(actorSchema.userCard.id, userCard.id));
    },
    mutationKey: ["userCardActions", "skip"],
  });

  const resetMutation = useMutation({
    mutationFn: async (target: UserCardActionTarget): Promise<void> => {
      const userCard = await resolveUserCard(target);
      await actorDb(import.meta.location).transaction(async (tx) => {
        const current = await tx.query.userCard.findFirst({ where: { id: { eq: userCard.id } } });
        if (current === undefined) {
          throw new Error(`User card ${userCard.id} does not exist`);
        }
        const reviewRows = await tx.query.userCardReview.findMany({
          columns: { fsrsCardReviewLogId: true, id: true },
          where: { cardId: { eq: userCard.id } },
        });
        const reviewLogIds = reviewRows.map((row) => row.fsrsCardReviewLogId);

        if (isNonEmpty(reviewLogIds)) {
          await tx
            .softDelete(actorSchema.fsrsCardReviewLog)
            .where(inArray(actorSchema.fsrsCardReviewLog.id, reviewLogIds));
          await tx
            .softDelete(actorSchema.userCardReview)
            .where(eq(actorSchema.userCardReview.cardId, userCard.id));
        }

        await tx
          .update(actorSchema.userCard)
          .set({
            fsrsCardId: null,
            manuallyMasteredAt: null,
            startedStudyingAt: null,
          })
          .where(eq(actorSchema.userCard.id, userCard.id));
        // A suspended device may still upload a card/review referencing this
        // schedule. Its tombstone preserves that relationship during replay.
        if (current.fsrsCardId !== null) {
          await tx
            .softDelete(actorSchema.fsrsCard)
            .where(eq(actorSchema.fsrsCard.id, current.fsrsCardId));
        }
      });
    },
    mutationKey: ["userCardActions", "reset"],
  });

  const markMasteredMutation = useMutation({
    mutationFn: async (target: UserCardActionTarget): Promise<void> => {
      const nowIso = new Date().toISOString();
      const userCard = await resolveUserCard(target);
      await actorDb(import.meta.location)
        .update(actorSchema.userCard)
        .set({ deletedAt: null, manuallyMasteredAt: nowIso })
        .where(eq(actorSchema.userCard.id, userCard.id));
    },
    mutationKey: ["userCardActions", "markMastered"],
  });

  const unmarkMasteredMutation = useMutation({
    mutationFn: async (target: UserCardActionTarget): Promise<void> => {
      const userCard = await resolveUserCard(target);
      await actorDb(import.meta.location)
        .update(actorSchema.userCard)
        .set({ deletedAt: null, manuallyMasteredAt: null })
        .where(eq(actorSchema.userCard.id, userCard.id));
    },
    mutationKey: ["userCardActions", "unmarkMastered"],
  });

  return {
    markMasteredMutation,
    resetMutation,
    skipMutation,
    unmarkMasteredMutation,
  };
}
